/** The way out of the code step for a visitor whose code email never arrives:
 * "Already have a password?", then a password field.
 *
 * Two deliberate delays, both product rules rather than mechanics. The link is
 * hidden until the visitor has been on the code step a little while — the code
 * is what we want them to use, and an alternative offered immediately competes
 * with it (and, for a new visitor, with the email verification the OTP flow
 * exists to get). The field itself stays behind the link, so the card a visitor
 * sees while waiting is unchanged.
 *
 * Same shape as resendButton.tsx, and for the same reason: the thing that starts
 * the timer owns stopping it, so a step left after five seconds can't reveal a
 * password field inside a hidden section.
 */

import { field, gatedButton } from "../components/form";
import { h } from "../lib/jsx";

const LINK_LABEL = "Already have a password?";

export interface PasswordFallback {
  el: HTMLElement;
  /** Start the delay before the link appears. */
  arm(): void;
  /** Stop waiting. Idempotent, so the flow can call it whenever the code step
   * is left without knowing whether the link had appeared. */
  stop(): void;
  /** Back to the unrevealed state, and re-gate the CTA — `clearBusy` re-enables
   * every control in the section, this button included. */
  reset(): void;
  /** The password as typed. */
  value(): string;
}

export function createPasswordFallback(opts: {
  /** How long the visitor waits before the link appears. */
  seconds: number;
  /** The revealed form's submit — wired straight to the element, so the flow
   * handles it exactly like its other steps' submits. */
  onSubmit(e: Event): void;
  /** The link has appeared, for analytics. Fires once per arm(). */
  onOffered?(): void;
}): PasswordFallback {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const passwordField = field("otp-password", "Password", {
    name: "password",
    type: "password",
    autocomplete: "current-password",
    placeholder: "Enter your password",
  });
  const submitBtn = gatedButton({
    label: "Sign in",
    ready: () => passwordField.input.value.length > 0,
    watch: [passwordField.input],
  });

  const link = (
    <button class="otp-btn otp-btn-link" type="button" hidden onClick={reveal}>
      {LINK_LABEL}
    </button>
  ) as HTMLButtonElement;

  const form = (
    <form novalidate hidden onSubmit={opts.onSubmit}>
      {passwordField.nodes}
      {submitBtn.el}
    </form>
  ) as HTMLFormElement;

  const el = (
    <div class="otp-fallback">
      {link}
      {form}
    </div>
  ) as HTMLElement;

  function reveal(): void {
    link.hidden = true;
    form.hidden = false;
    submitBtn.refresh();
    passwordField.input.focus();
  }

  function stop(): void {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  }

  return {
    el,
    arm() {
      stop();
      timer = setTimeout(() => {
        timer = null;
        link.hidden = false;
        opts.onOffered?.();
      }, opts.seconds * 1000);
    },
    stop,
    reset() {
      link.hidden = true;
      form.hidden = true;
      passwordField.input.value = "";
      submitBtn.refresh();
    },
    value() {
      return passwordField.input.value;
    },
  };
}
